import React from 'react';
import { Trash2 } from 'lucide-react';
import { AppConfirmDialog } from '../../../../components/AppConfirmDialog';
import { InlineLoadingState, LoadingSpinner } from '../../../../components/LoadingSpinner';
import { folderRailName, isCategoryFolder, replaceFolderLeafName } from '../../../inbox/folderNames';
import { SettingsCard } from '../../components/SettingsCard';
import { SettingsCardSavedStatus } from '../../components/SettingsCardSavedStatus';
import { SettingsToggle } from '../../components/SettingsToggle';
import {
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
  SETTINGS_TABLE_HEADER,
} from '../../utils/settingsSectionStyles';
import type { InboxAccount } from './types';

type FolderRow = {
  id: string;
  external_folder_id?: string;
  name: string;
  display_name: string;
  attributes: string[];
  system_folder: boolean;
  description: string | null;
};

type FolderDraft = { name: string; description: string };

const SYSTEM_TOKENS = new Set([
  'INBOX',
  'SENT',
  'DRAFT',
  'DRAFTS',
  'TRASH',
  'SPAM',
  'JUNK',
  'ARCHIVE',
  'STARRED',
  'UNREAD',
  'IMPORTANT',
  'POSTEINGANG',
  'GESENDET',
  'ENTWÜRFE',
  'PAPIERKORB',
  'ARCHIV',
]);

function isAccountConnected(acc: InboxAccount): boolean {
  return Boolean(acc.nylas_grant_id) || Boolean(acc.is_demo_simulated_connection);
}

function isCustomFolder(folder: FolderRow): boolean {
  if (!isCategoryFolder(folder)) return false;
  const id = String(folder.external_folder_id ?? '').toUpperCase();
  const name = String(folder.name ?? '').toUpperCase();
  const display = String(folder.display_name ?? '').toUpperCase();
  if (SYSTEM_TOKENS.has(id) || SYSTEM_TOKENS.has(name) || SYSTEM_TOKENS.has(display)) return false;
  const leaf = folderRailName(folder.name || folder.display_name).label;
  if (leaf.toLowerCase().endsWith('.pst')) return false;
  return true;
}

/** Name | Beschreibung | Löschen — auf dem Handy untereinander. */
const FOLDER_GRID = 'grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_2.25rem] sm:items-center';

function draftsFromFolders(folders: FolderRow[]): Record<string, FolderDraft> {
  const next: Record<string, FolderDraft> = {};
  for (const folder of folders) {
    next[folder.id] = {
      name: folderRailName(folder.name || folder.display_name).label,
      description: folder.description ?? '',
    };
  }
  return next;
}

/** on_done (Standard): die KI merkt den Ordner nur vor, verschoben wird bei „Erledigt“. */
function FilingModeToggle({ account }: { account: InboxAccount }) {
  const [onDone, setOnDone] = React.useState(account.filing_mode !== 'immediate');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setOnDone(account.filing_mode !== 'immediate');
  }, [account.filing_mode]);

  const save = async (next: boolean) => {
    const previous = onDone;
    setOnDone(next);
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/inbox/accounts/${encodeURIComponent(String(account.id))}/filing-mode`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ filing_mode: next ? 'on_done' : 'immediate' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error || 'Speichern fehlgeschlagen.');
      setOnDone((data as { filing_mode?: string }).filing_mode !== 'immediate');
    } catch (err) {
      setOnDone(previous);
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-b border-app-border pb-4">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className={SETTINGS_FIELD_LABEL}>Erst bei „Erledigt“ verschieben</p>
          <p className="mt-0.5 text-xs text-app-text-muted">
            {onDone
              ? 'Mails bleiben im Posteingang, bis Sie sie erledigen. Im Ordner sind sie schon vorher sichtbar.'
              : 'Die KI verschiebt neue Mails sofort in ihren Ordner.'}
          </p>
        </div>
        <SettingsToggle
          active={onDone}
          disabled={saving}
          onClick={() => void save(!onDone)}
          label="Erst bei Erledigt in den Ordner verschieben"
        />
      </div>
      {error ? <p className="mt-2 text-sm text-red-700">{error}</p> : null}
    </div>
  );
}

function AccountCategories({ account }: { account: InboxAccount }) {
  const [folders, setFolders] = React.useState<FolderRow[]>([]);
  const [drafts, setDrafts] = React.useState<Record<string, FolderDraft>>({});
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [draftName, setDraftName] = React.useState('');
  const [draftDescription, setDraftDescription] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [pendingDelete, setPendingDelete] = React.useState<FolderRow | null>(null);
  const [deleting, setDeleting] = React.useState(false);
  const savedTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const markSaved = React.useCallback(() => {
    setSaved(true);
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => setSaved(false), 2200);
  }, []);

  React.useEffect(() => () => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/inbox/accounts/${encodeURIComponent(String(account.id))}/folders`, {
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error || 'Ordner konnten nicht geladen werden.');
      const rows = ((Array.isArray(data.folders) ? data.folders : []) as FolderRow[])
        .filter(isCustomFolder)
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name, 'de'));
      setFolders(rows);
      setDrafts(draftsFromFolders(rows));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ordner konnten nicht geladen werden.');
      setFolders([]);
      setDrafts({});
    } finally {
      setLoading(false);
    }
  }, [account.id]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const dirtyFolders = React.useMemo(() => {
    return folders.filter((folder) => {
      const draft = drafts[folder.id];
      if (!draft) return false;
      const leaf = folderRailName(folder.name || folder.display_name).label;
      return draft.name.trim() !== leaf || draft.description.trim() !== (folder.description ?? '');
    });
  }, [drafts, folders]);

  const canCreate = Boolean(draftName.trim());
  const dirty = dirtyFolders.length > 0 || canCreate;
  const pendingDeleteName = pendingDelete
    ? drafts[pendingDelete.id]?.name.trim() || folderRailName(pendingDelete.name || pendingDelete.display_name).label
    : '';

  const persistFolder = async (folder: FolderRow, next: { name?: string; description?: string }) => {
    const body: Record<string, string> = {};
    if (next.name != null) body.name = next.name;
    if (next.description != null) body.description = next.description;
    const res = await fetch(`/api/inbox/folders/${encodeURIComponent(folder.id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error((data as { error?: string }).error || 'Speichern fehlgeschlagen.');
    return data as FolderRow;
  };

  const createFolder = async (name: string, description: string) => {
    const res = await fetch(`/api/inbox/accounts/${encodeURIComponent(String(account.id))}/folders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name, description }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error((data as { error?: string }).error || 'Ordner konnte nicht angelegt werden.');
  };

  const saveAll = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setError(null);
    try {
      for (const folder of dirtyFolders) {
        const draft = drafts[folder.id];
        if (!draft) continue;
        const nextName = replaceFolderLeafName(folder.name, draft.name);
        const nextDescription = draft.description.trim();
        await persistFolder(folder, { name: nextName, description: nextDescription });
      }
      if (canCreate) {
        await createFolder(draftName.trim(), draftDescription.trim());
        setDraftName('');
        setDraftDescription('');
      }
      await load();
      markSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await fetch(`/api/inbox/folders/${encodeURIComponent(pendingDelete.id)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error || 'Löschen fehlgeschlagen.');
      setPendingDelete(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Löschen fehlgeschlagen.');
    } finally {
      setDeleting(false);
    }
  };

  const updateDraft = (id: string, patch: Partial<FolderDraft>) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: { name: prev[id]?.name ?? '', description: prev[id]?.description ?? '', ...patch },
    }));
  };

  return (
    <div>
      <FilingModeToggle account={account} />
      <div className="pt-4">
        {error ? (
          <p className="mb-2 text-sm text-red-700">{error}</p>
        ) : null}
        {loading ? (
          <div className="py-2">
            <InlineLoadingState label="Ordner werden geladen …" className="justify-start" />
          </div>
        ) : (
          <div className="max-h-[28rem] overflow-auto app-scrollbar">
            <div className={`${FOLDER_GRID} ${SETTINGS_TABLE_HEADER} pb-1.5 max-sm:hidden`}>
              <span>Ordner</span>
              <span>Wann soll die KI ihn wählen?</span>
              <span className="sr-only">Löschen</span>
            </div>
            <ul className="space-y-2">
              {folders.map((folder) => {
                const draft = drafts[folder.id] ?? { name: '', description: '' };
                return (
                  <li key={folder.id} className={FOLDER_GRID}>
                    <input
                      type="text"
                      value={draft.name}
                      onChange={(event) => updateDraft(folder.id, { name: event.target.value })}
                      className={SETTINGS_INPUT_CLASS}
                      aria-label={`Name ${draft.name}`}
                    />
                    <input
                      type="text"
                      value={draft.description}
                      onChange={(event) => updateDraft(folder.id, { description: event.target.value })}
                      placeholder="z. B. Rechnungen und Mahnungen von Lieferanten"
                      className={SETTINGS_INPUT_CLASS}
                      aria-label={`Beschreibung ${draft.name}`}
                    />
                    <button
                      type="button"
                      onClick={() => setPendingDelete(folder)}
                      disabled={saving || deleting}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400"
                      title="Ordner löschen"
                      aria-label={`Ordner ${draft.name || 'ohne Namen'} löschen`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                );
              })}
              <li className={FOLDER_GRID}>
                <input
                  type="text"
                  value={draftName}
                  onChange={(event) => setDraftName(event.target.value)}
                  placeholder={folders.length === 0 ? 'Ersten Ordner anlegen' : 'Neuer Ordner'}
                  className={`${SETTINGS_INPUT_CLASS} border-dashed`}
                  aria-label="Name des neuen Ordners"
                />
                <input
                  type="text"
                  value={draftDescription}
                  onChange={(event) => setDraftDescription(event.target.value)}
                  placeholder="Kurze Beschreibung für die KI"
                  className={`${SETTINGS_INPUT_CLASS} border-dashed`}
                  aria-label="Beschreibung des neuen Ordners"
                />
                <span className="max-sm:hidden" />
              </li>
            </ul>
          </div>
        )}
      </div>
      <div className="mt-4 flex items-center justify-end gap-3">
        <SettingsCardSavedStatus saved={saved} className="text-xs" />
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={() => void saveAll()}
          className={`${SETTINGS_BTN_PRIMARY} disabled:opacity-50`}
        >
          {saving ? <LoadingSpinner /> : null}
          Speichern
        </button>
      </div>
      <AppConfirmDialog
        open={pendingDelete != null}
        onClose={() => {
          if (!deleting) setPendingDelete(null);
        }}
        title="Ordner löschen?"
        description={
          pendingDeleteName
            ? `„${pendingDeleteName}“ wird im Postfach entfernt. Zugeordnete Mails bleiben erhalten.`
            : 'Der Ordner wird im Postfach entfernt. Zugeordnete Mails bleiben erhalten.'
        }
        confirmLabel="Löschen"
        destructive
        isLoading={deleting}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
}

export function InboxCategorySettingsCard({ accounts }: { accounts: InboxAccount[] }) {
  const connected = accounts.filter(isAccountConnected);
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const activeAccountId = connected.some((acc) => String(acc.id) === activeId)
    ? activeId
    : connected[0] ? String(connected[0].id) : null;

  return (
    <div id="kategorien">
      <SettingsCard
        title="Ordner"
        description="Die KI sortiert eingehende Mails anhand Ihrer Beschreibung in diese Ordner. Die Beschreibung bleibt in MeisterPfad."
      >
        {connected.length === 0 ? (
          <p className="text-sm text-app-text-subtle">Verbinden Sie ein Postfach, um Ordner zu pflegen.</p>
        ) : (
          <>
            {connected.length > 1 ? (
              <div
                className="mb-4 flex min-w-0 flex-wrap border-b border-app-border"
                role="tablist"
                aria-label="Postfach"
              >
                {connected.map((acc) => {
                  const id = String(acc.id);
                  const selected = id === activeAccountId;
                  return (
                    <button
                      key={id}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      id={`inbox-folders-tab-${id}`}
                      aria-controls={`inbox-folders-panel-${id}`}
                      onClick={() => setActiveId(id)}
                      className={`-mb-px shrink-0 border-b-2 px-3.5 py-2.5 text-sm font-medium transition-colors first:pl-0 ${
                        selected
                          ? 'border-pipedrive-blue text-pipedrive-blue'
                          : 'border-transparent text-app-text-subtle hover:text-app-text'
                      }`}
                    >
                      {acc.email}
                    </button>
                  );
                })}
              </div>
            ) : null}
            {/* Alle Postfächer bleiben gemountet, damit ungespeicherte Eingaben beim Tab-Wechsel erhalten bleiben. */}
            {connected.map((acc) => {
              const id = String(acc.id);
              return (
                <div
                  key={id}
                  role={connected.length > 1 ? 'tabpanel' : undefined}
                  id={`inbox-folders-panel-${id}`}
                  aria-labelledby={connected.length > 1 ? `inbox-folders-tab-${id}` : undefined}
                  hidden={id !== activeAccountId}
                >
                  <AccountCategories account={acc} />
                </div>
              );
            })}
          </>
        )}
      </SettingsCard>
    </div>
  );
}
